'use client';

import { twMerge } from 'tailwind-merge';

import { useIsMobileScreenWidthOrThinner } from '../../helpers/hooks/useScreenWidth';
import { Heading } from '../Heading/Heading';

import { useState } from 'react';
import { FishingReportCTASection } from './components/FishingReportCTASection';
import { FishingReport, FishingReportCard } from './components/FishingReportCard';
import {
  FISHINGREPORT_SECTION_MAX_CATCHES_DESKTOP,
  FISHINGREPORT_SECTION_MAX_CATCHES_MOBILE,
  FISHINGREPORT_SECTION_MAX_CATCHES_TABLET,
} from './constants/constants';
import { Pill } from '../Clickables/Pill/Pill';
import { AppDownloadModal } from '../AppDownloadModal/AppDownloadModal';
import appdownloadPhone from './assets/AppdownloadPhone.png';
import appdownloadPhoneMobile from './assets/AppdownloadPhoneMobile.png';
import { useIsTouchDevice } from '../../helpers/hooks/useIsTouchDevice';
import { useAnalytics } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../helpers/analytics/trackEvent';
interface IProps {
  className?: string;
  id: string;
  fakeFilters?: string[];
  fishingReports: FishingReport[];
  maxCatchesDesktop?: number;
  maxCatchesMobile?: number;
  maxCatchesTablet?: number;
  showFishingWaterName?: boolean;
  sourceId?: string;
  title: string;
}

export const FishingReportsGrid = ({
  className,
  id = 'fishing-reports-grid',
  fakeFilters,
  fishingReports,
  maxCatchesDesktop = FISHINGREPORT_SECTION_MAX_CATCHES_DESKTOP,
  maxCatchesMobile = FISHINGREPORT_SECTION_MAX_CATCHES_MOBILE,
  maxCatchesTablet = FISHINGREPORT_SECTION_MAX_CATCHES_TABLET,
  showFishingWaterName = false,
  sourceId,
  title = 'Fishing reports',
}: IProps) => {
  const isTouchDevice = useIsTouchDevice();
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const [activeFakeFilter, setActiveFakeFilter] = useState<number | undefined>();
  const { currentPage } = useAnalytics();

  const isMobileScreenWidthOrThinner = useIsMobileScreenWidthOrThinner();
  const hasFakeFilters = fakeFilters && fakeFilters.length > 0;

  const handleFakeFilterClick = (index: number) => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'FishingReportsGridFakeFilters',
        source_id: sourceId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setActiveFakeFilter(index);
    setIsAppDownloadModalOpen(true);
  };

  return (
    <div
      id={id}
      className={twMerge(
        'w-full scroll-mt-(--navigation-scrolltop-offset-mobile) px-6 sm:scroll-mt-(--navigation-scrolltop-offset) sm:px-0',
        className,
      )}
    >
      <Heading className={hasFakeFilters ? 'mb-4' : 'mb-6 sm:mb-10'} variant="sm" as="h2">
        {title}
      </Heading>
      {hasFakeFilters && (
        <div
          className={`-mx-6 mb-4 flex flex-row flex-nowrap gap-2 overflow-auto whitespace-nowrap sm:mx-0 ${isTouchDevice ? 'scrollbar-none' : ''}`}
        >
          {fakeFilters.map((filter, index) => (
            <Pill
              className={`${index === 0 ? 'ml-6 sm:ml-0' : ''} ${index === fakeFilters.length - 1 ? 'mr-6 sm:mr-0' : ''}`}
              variant="whiteBg"
              size="sm"
              isActive={activeFakeFilter === index}
              key={filter}
              onClick={() => {
                handleFakeFilterClick(index);
              }}
            >
              {filter}
            </Pill>
          ))}
        </div>
      )}
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-12 sm:gap-4">
        {fishingReports.map((post, index) => (
          <FishingReportCard
            key={post._id}
            post={post}
            className={`col-span-2 sm:col-span-4 md:col-span-3 ${index >= maxCatchesMobile ? 'hidden' : ''} ${index >= maxCatchesTablet ? 'sm:hidden' : ''} ${index >= maxCatchesDesktop ? 'md:hidden' : ''}`}
            showFishingWaterName={showFishingWaterName}
          />
        ))}
      </div>
      <FishingReportCTASection catchCount={fishingReports.length} />
      {hasFakeFilters && (
        <AppDownloadModal
          appDownloadSource="FishingReportsGrid"
          appDownloadSourceId={sourceId}
          closeButtonVariant={isMobileScreenWidthOrThinner ? 'textNone' : 'controlWhiteBg'}
          modalHeader={'Find more fish, faster – Use catch filters in the Fishbrain app'}
          modalHeaderClassName="sm:mr-16 md:mr-16"
          modalHeaderVariant="sm"
          modalText=""
          isModalOpen={isAppDownloadModalOpen}
          image={appdownloadPhone}
          imageMobile={appdownloadPhoneMobile}
          imageWrapperClassName="bg-tuna-tint px-8 sm:p-6 md:mr-4"
          onCloseModal={() => {
            setIsAppDownloadModalOpen(false);
            setActiveFakeFilter(undefined);
          }}
          textWrapperClassName="mt-6 sm:pb-0 md:pr-4 md:pb-4"
          showQRCodeHint
          width="700px"
        ></AppDownloadModal>
      )}
    </div>
  );
};
